// @owners { team: patient-health }
import { COLORS } from '@alto/design-library-tokens';
import { ListPage, Row, Separator } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { type ViewToken } from 'react-native';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useFetchAllNotifications } from '../../alto-assistant';
import { useMarkAsRead } from '../queries/useMarkAsRead';
import { NotificationCenterListItem } from './NotificationCenterListItem';

const viewabilityConfig = {
  minimumViewTime: 500,
  itemVisiblePercentThreshold: 90,
};

export const NotificationCenter = () => {
  const { goBack, getParam } = useNavigation<'RouteNotificationCenter'>();
  const { trackEvent } = useAnalytics();
  const { unreadNotificationsCount } = useFetchAllNotifications();
  const origin = getParam('origin') || '';
  const { trackPageView } = useAnalytics();
  const { markAsRead } = useMarkAsRead();
  const { data, fetchNextPage, hasNextPage, isPending, isFetching } = useFetchAllNotifications();

  const handleFetchMore = useCallback(async () => {
    if (hasNextPage && !isPending && !isFetching) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isPending, isFetching]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.NOTIFICATION_CENTER_VIEWED,
      params: {
        origin,
        unreadNotificationsCount,
      },
    });
  }, [origin, trackPageView, unreadNotificationsCount]);

  const onViewableItemsChanged = useMemo(
    () =>
      debounce((info: { viewableItems: ViewToken[]; changed: ViewToken[] }) => {
        const unreadViewableIDs = info.viewableItems
          .filter((item) => !item.item.read_at)
          .map((item) => item.item.wundercom_message_id);
        if (unreadViewableIDs.length) {
          trackEvent({
            event: EVENTS.NOTIFICATION_VIEWED,
            params: {
              wundercom_message_ids: unreadViewableIDs,
            },
          });
          markAsRead({ wundercom_message_ids: unreadViewableIDs });
        }
      }, 500),
    [markAsRead, trackEvent],
  );

  return (
    <ListPage.Root>
      <ListPage.Content.FlatList
        data={data}
        ItemSeparatorComponent={Separator}
        ListHeaderComponent={
          <ListPage.Header.Root
            backgroundColor={COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST}
            onDismiss={goBack}
          >
            <ListPage.Header.Content.Root>
              <Row center>
                <ListPage.Header.Content.Title>Notifications</ListPage.Header.Content.Title>
              </Row>
            </ListPage.Header.Content.Root>
          </ListPage.Header.Root>
        }
        renderItem={({ item }) => <NotificationCenterListItem {...item} />}
        keyExtractor={(item) => item.wundercom_message_id.toString()}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onEndReached={handleFetchMore}
        onEndReachedThreshold={0.1}
        viewabilityConfig={viewabilityConfig}
        onViewableItemsChanged={onViewableItemsChanged}
      />
    </ListPage.Root>
  );
};
